/**
 * URL builder utilities for the tenant/project-scoped route model.
 *
 * All helpers return root-relative paths (no BASE_URL prefix).
 * Wrap with `appPath()` from `@/lib/utils` when used in `window.location` assignments.
 */

// Any fixed origin works: it only anchors relative parsing outside a browser.
const RETURN_PATH_BASE = 'https://return-path.invalid';
// Browsers read `\` as `/` in http(s) URLs and strip tabs and newlines while parsing,
// so `/\host` and `/<tab>/host` both reach another origin.
const UNSAFE_RETURN_PATH_CHARACTER = /[\\\p{Cc}]/u;

/**
 * Parse a URL-sourced navigation destination (`?returnTo=`, `?redirect=`, router
 * state) into a root-relative path that cannot leave the application.
 *
 * Returns the normalized path with its query and fragment, or `null` when the value
 * is absent, absolute, protocol-relative, or contains characters a browser would
 * reinterpret. Callers fall back to their own default destination on `null`.
 */
export function parseReturnPath(value: string | null): string | null {
  if (
    value === null ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    UNSAFE_RETURN_PATH_CHARACTER.test(value)
  ) {
    return null;
  }
  const destination = new URL(value, RETURN_PATH_BASE);
  // Dot segments can collapse into a leading `//`, which is an authority once reused.
  if (destination.pathname.startsWith('//')) {
    return null;
  }
  return `${destination.pathname}${destination.search}${destination.hash}`;
}

export function tenantPath(slug: string, path = ''): string {
  const normalized = path.startsWith('/') ? path : path.length > 0 ? `/${path}` : '';
  return `/${slug}${normalized}`;
}

/**
 * The Settings tab where anyone who may view charge codes sees the trial and
 * billing posture, and anyone who may submit one can. Every "add a charge
 * code" affordance lands here, never on the Profile tab.
 */
export function billingSettingsPath(slug: string): string {
  return `${tenantPath(slug, '/settings')}?tab=billing`;
}

/** The Settings tab where superadmins edit the workspace rate card, Marketplace prices included. */
export function pricingSettingsPath(slug: string): string {
  return `${tenantPath(slug, '/settings')}?tab=pricing-billing`;
}

export function projectPath(slug: string, projectId: string, path = ''): string {
  const normalized = path.startsWith('/') ? path : path.length > 0 ? `/${path}` : '';
  return `/${slug}/projects/${projectId}${normalized}`;
}

/** The overview's search key that opens the project's edit form on arrival. */
export const PROJECT_EDIT_PARAM = 'edit';

/** The project overview with its edit form open, where the charge code is changed. */
export function projectEditPath(slug: string, projectId: string): string {
  return `${projectPath(slug, projectId)}?${PROJECT_EDIT_PARAM}=project`;
}

export function projectTemplateResultPath(
  slug: string,
  projectId: string,
  templateId: string,
  sourceSearch = '',
): string {
  const source = new URLSearchParams(sourceSearch);
  const target = new URLSearchParams();
  target.set('template', templateId);

  source.forEach((value, key) => {
    if (key !== 'template' && key !== 'legacy') {
      target.append(key, value);
    }
  });

  return `${projectPath(slug, projectId, '/documents')}?${target.toString()}`;
}

const DOCUMENTS_PAGE_PATH = /^\/[^/]+(?:\/projects\/[^/]+)?\/documents\/?$/;

/**
 * Whether a location opens a result: the documents page (project-scoped, or
 * tenant-wide) with a `template` selected. The URL alone answers it, so the
 * app shell can lay out a result's workspace chrome in its first frame,
 * before the page's code or data arrive.
 */
export function isTemplateResultLocation(location: { readonly pathname: string; readonly search: string }): boolean {
  return DOCUMENTS_PAGE_PATH.test(location.pathname) && new URLSearchParams(location.search).get('template') !== null;
}

const TEMPLATE_EDITOR_PATH = /^\/[^/]+(?:\/projects\/[^/]+(?:\/tsa-workspace)?)?\/templates\/(?:new|[^/]+\/edit)\/?$/;

/** Whether a location opens the template editor, in any of its scopes. */
function isTemplateEditorLocation(location: { readonly pathname: string }): boolean {
  return TEMPLATE_EDITOR_PATH.test(location.pathname);
}

/**
 * Whether a location is a workspace the shell gives the whole main region and
 * the sidebar's width: an open result or the template editor.
 */
export function isWorkspaceLocation(location: { readonly pathname: string; readonly search: string }): boolean {
  return isTemplateResultLocation(location) || isTemplateEditorLocation(location);
}

export function projectExecutionLogPath(
  slug: string,
  projectId: string,
  reference?: { readonly analysisId?: string; readonly runId?: string },
): string {
  const search = new URLSearchParams();
  search.set('tab', 'logs');
  if (reference?.analysisId !== undefined) {
    search.set('analysis', reference.analysisId);
  }
  if (reference?.runId !== undefined) {
    search.set('run', reference.runId);
  }
  return `${projectPath(slug, projectId, '/settings')}?${search.toString()}`;
}
